import { useEffect, useState } from "react";

import type {
  PlatformConsoleTenantSummaryV1,
  WorkforceSessionSummaryV1,
} from "@amplecare/contracts";
import { designTokens } from "@amplecare/design-tokens";
import { Pressable, ScrollView, Text, TextInput, View } from "react-native";

import type { MobileRuntimeConfig } from "../../config/runtime-config.js";
import { styles } from "../AppShell.styles.js";
import {
  createPlatformConsoleFirstAdmin,
  createPlatformConsoleTenant,
  fetchPlatformConsoleTenants,
} from "../platform-console-client.js";
import type { AppShellTheme } from "../shell-core.js";
import {
  createWebViewportMinHeightStyle,
  renderWorkbenchActionButton,
} from "../shell-primitives.js";

export type PlatformConsolePageProps = {
  runtimeConfig: MobileRuntimeConfig;
  theme: AppShellTheme;
  tokens: typeof designTokens;
  layout: typeof designTokens.semanticLayout;
  isMediumLayout: boolean;
  activeWorkforceSession: WorkforceSessionSummaryV1 | null;
  canSwitchWorkspaces: boolean;
  beginWorkspaceSelection: () => void;
  onSignOut: () => void;
  onSessionExpired: () => void;
};

function resolvePlatformConsoleErrorText(error: unknown): string {
  return error instanceof Error && error.message.trim()
    ? error.message.trim()
    : "Something went wrong. Please try again.";
}

function isPlatformConsoleUnauthorized(error: unknown): boolean {
  return (
    error instanceof Error &&
    (error.message.includes("failed-401") ||
      error.message.includes("failed-403"))
  );
}

export function PlatformConsolePage(props: PlatformConsolePageProps) {
  const {
    runtimeConfig,
    theme,
    tokens,
    layout,
    isMediumLayout,
    activeWorkforceSession,
    canSwitchWorkspaces,
    beginWorkspaceSelection,
    onSignOut,
    onSessionExpired,
  } = props;

  const [tenants, setTenants] = useState<PlatformConsoleTenantSummaryV1[]>([]);
  const [generatedAt, setGeneratedAt] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [loadErrorMessage, setLoadErrorMessage] = useState<string | null>(null);
  const [refreshNonce, setRefreshNonce] = useState<number>(0);

  const [tenantKeyDraft, setTenantKeyDraft] = useState<string>("");
  const [tenantSlugDraft, setTenantSlugDraft] = useState<string>("");
  const [tenantNameDraft, setTenantNameDraft] = useState<string>("");
  const [isCreatingTenant, setIsCreatingTenant] = useState<boolean>(false);
  const [createTenantError, setCreateTenantError] = useState<string | null>(
    null,
  );
  const [createTenantNotice, setCreateTenantNotice] = useState<string | null>(
    null,
  );

  const [adminTenantKeyDraft, setAdminTenantKeyDraft] = useState<string>("");
  const [adminEmailDraft, setAdminEmailDraft] = useState<string>("");
  const [adminNameDraft, setAdminNameDraft] = useState<string>("");
  const [adminPasswordDraft, setAdminPasswordDraft] = useState<string>("");
  const [isCreatingAdmin, setIsCreatingAdmin] = useState<boolean>(false);
  const [createAdminError, setCreateAdminError] = useState<string | null>(null);
  const [createdAdminSubjectKey, setCreatedAdminSubjectKey] = useState<
    string | null
  >(null);

  useEffect(() => {
    const abortController = new AbortController();

    setIsLoading(true);
    setLoadErrorMessage(null);

    void fetchPlatformConsoleTenants(runtimeConfig, abortController.signal)
      .then((payload) => {
        if (abortController.signal.aborted) {
          return;
        }

        setTenants(payload.tenants);
        setGeneratedAt(payload.generatedAt);
        setIsLoading(false);
      })
      .catch((error: unknown) => {
        if (abortController.signal.aborted) {
          return;
        }

        if (isPlatformConsoleUnauthorized(error)) {
          onSessionExpired();
          return;
        }

        setLoadErrorMessage(resolvePlatformConsoleErrorText(error));
        setIsLoading(false);
      });

    return () => {
      abortController.abort();
    };
  }, [
    runtimeConfig.sessionApiBaseUrl,
    runtimeConfig.workforceSessionSubjectKey,
    refreshNonce,
  ]);

  const handleRefresh = () => {
    setRefreshNonce((value) => value + 1);
  };

  const handleCreateTenant = () => {
    const trimmedTenantKey = tenantKeyDraft.trim();
    const trimmedTenantSlug = tenantSlugDraft.trim();
    const trimmedDisplayName = tenantNameDraft.trim();

    if (!trimmedTenantKey || !trimmedTenantSlug || !trimmedDisplayName) {
      setCreateTenantError(
        "Tenant key, slug, and display name are all required.",
      );
      setCreateTenantNotice(null);
      return;
    }

    setIsCreatingTenant(true);
    setCreateTenantError(null);
    setCreateTenantNotice(null);

    void createPlatformConsoleTenant(runtimeConfig, {
      tenantKey: trimmedTenantKey,
      tenantSlug: trimmedTenantSlug,
      displayName: trimmedDisplayName,
    })
      .then((payload) => {
        setCreateTenantNotice(
          `Created tenant "${payload.tenant.displayName}" (${payload.tenant.tenantKey}).`,
        );
        setTenantKeyDraft("");
        setTenantSlugDraft("");
        setTenantNameDraft("");
        setAdminTenantKeyDraft(payload.tenant.tenantKey);
        setIsCreatingTenant(false);
        handleRefresh();
      })
      .catch((error: unknown) => {
        if (isPlatformConsoleUnauthorized(error)) {
          onSessionExpired();
          return;
        }

        setCreateTenantError(resolvePlatformConsoleErrorText(error));
        setIsCreatingTenant(false);
      });
  };

  const handleCreateAdmin = () => {
    const trimmedTenantKey = adminTenantKeyDraft.trim();
    const trimmedEmail = adminEmailDraft.trim();
    const trimmedDisplayName = adminNameDraft.trim();

    if (
      !trimmedTenantKey ||
      !trimmedEmail ||
      !trimmedDisplayName ||
      !adminPasswordDraft
    ) {
      setCreateAdminError(
        "Tenant key, email, display name, and temporary password are all required.",
      );
      setCreatedAdminSubjectKey(null);
      return;
    }

    setIsCreatingAdmin(true);
    setCreateAdminError(null);
    setCreatedAdminSubjectKey(null);

    void createPlatformConsoleFirstAdmin(runtimeConfig, {
      tenantKey: trimmedTenantKey,
      email: trimmedEmail,
      displayName: trimmedDisplayName,
      temporaryPassword: adminPasswordDraft,
    })
      .then((payload) => {
        setCreatedAdminSubjectKey(payload.subjectKey);
        setAdminEmailDraft("");
        setAdminNameDraft("");
        setAdminPasswordDraft("");
        setIsCreatingAdmin(false);
        handleRefresh();
      })
      .catch((error: unknown) => {
        if (isPlatformConsoleUnauthorized(error)) {
          onSessionExpired();
          return;
        }

        setCreateAdminError(resolvePlatformConsoleErrorText(error));
        setIsCreatingAdmin(false);
      });
  };

  const cardBaseStyle = {
    backgroundColor: theme.surface,
    borderColor: theme.borderSubtle,
    borderRadius: layout.cardRadius,
    gap: tokens.spacing.md,
    padding: tokens.spacing.lg,
  } as const;

  const renderField = (input: {
    labelText: string;
    value: string;
    onChangeText: (value: string) => void;
    placeholder: string;
    editable: boolean;
    secureTextEntry?: boolean;
    autoCapitalizeNone?: boolean;
    isEmail?: boolean;
  }) => (
    <View style={[styles.listGroup, { gap: tokens.spacing.xs }]}>
      <Text
        style={[
          styles.metricLabel,
          {
            color: theme.textSecondary,
            fontFamily: layout.bodyFontFamily,
            fontSize: tokens.typography.fontSize.xs,
            textTransform: "uppercase",
          },
        ]}
      >
        {input.labelText}
      </Text>
      <TextInput
        accessibilityLabel={input.labelText}
        autoCapitalize={
          input.autoCapitalizeNone || input.isEmail ? "none" : "sentences"
        }
        editable={input.editable}
        keyboardType={input.isEmail ? "email-address" : "default"}
        onChangeText={input.onChangeText}
        placeholder={input.placeholder}
        placeholderTextColor={theme.textTertiary}
        secureTextEntry={input.secureTextEntry ?? false}
        style={[
          styles.searchInput,
          {
            backgroundColor: theme.background,
            borderColor: theme.borderStrong,
            borderRadius: layout.controlRadius,
            borderWidth: 1,
            color: theme.textPrimary,
            fontFamily: layout.bodyFontFamily,
            fontSize: tokens.typography.fontSize.sm,
            minHeight: 40,
            paddingHorizontal: tokens.spacing.md,
            paddingVertical: tokens.spacing.sm,
          },
        ]}
        value={input.value}
      />
    </View>
  );

  const renderInlineMessage = (
    messageText: string,
    toneColor: string,
  ) => (
    <Text
      style={[
        styles.badgeText,
        {
          color: toneColor,
          fontFamily: layout.bodyFontFamily,
          fontSize: tokens.typography.fontSize.xs,
        },
      ]}
    >
      {messageText}
    </Text>
  );

  const renderCountTile = (labelText: string, value: number) => (
    <View
      key={labelText}
      style={{
        backgroundColor: theme.background,
        borderColor: theme.borderSubtle,
        borderRadius: layout.controlRadius,
        borderWidth: 1,
        flexGrow: 1,
        flexBasis: 88,
        gap: 2,
        padding: tokens.spacing.sm,
      }}
    >
      <Text
        style={[
          styles.metricLabel,
          {
            color: theme.textSecondary,
            fontFamily: layout.bodyFontFamily,
            fontSize: tokens.typography.fontSize.xs,
            textTransform: "uppercase",
          },
        ]}
      >
        {labelText}
      </Text>
      <Text
        style={[
          styles.metricValue,
          {
            color: theme.textPrimary,
            fontFamily: layout.bodyFontFamily,
            fontSize: tokens.typography.fontSize.lg,
          },
        ]}
      >
        {value}
      </Text>
    </View>
  );

  const renderTenantRow = (tenant: PlatformConsoleTenantSummaryV1) => {
    const statusColor =
      tenant.status === "active"
        ? theme.success
        : tenant.status === "suspended"
          ? theme.warning
          : theme.textSecondary;

    return (
      <View
        key={tenant.tenantKey}
        style={{
          backgroundColor: theme.background,
          borderColor: theme.borderSubtle,
          borderRadius: layout.controlRadius,
          borderWidth: 1,
          gap: tokens.spacing.sm,
          padding: tokens.spacing.md,
        }}
      >
        <View
          style={{
            alignItems: isMediumLayout ? "center" : "flex-start",
            flexDirection: isMediumLayout ? "row" : "column",
            gap: tokens.spacing.sm,
            justifyContent: "space-between",
          }}
        >
          <View style={{ flexShrink: 1, gap: 2 }}>
            <Text
              style={[
                styles.sectionTitle,
                {
                  color: theme.textPrimary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: tokens.typography.fontSize.md,
                },
              ]}
            >
              {tenant.displayName}
            </Text>
            <Text
              style={{
                color: theme.textSecondary,
                fontFamily: tokens.typography.fontFamily.mono,
                fontSize: tokens.typography.fontSize.xs,
              }}
            >
              {tenant.tenantKey}
              {tenant.slug ? ` · ${tenant.slug}` : ""}
            </Text>
          </View>
          <View
            style={{
              alignItems: "center",
              flexDirection: "row",
              gap: tokens.spacing.sm,
            }}
          >
            <View
              style={[
                styles.badge,
                {
                  backgroundColor: theme.surfaceMuted,
                  borderColor: statusColor,
                  borderRadius: layout.controlRadius,
                  borderWidth: 1,
                  paddingHorizontal: tokens.spacing.sm,
                  paddingVertical: 2,
                },
              ]}
            >
              <Text
                style={[
                  styles.badgeText,
                  {
                    color: statusColor,
                    fontFamily: layout.bodyFontFamily,
                    textTransform: "uppercase",
                  },
                ]}
              >
                {tenant.status}
              </Text>
            </View>
            <Pressable
              accessibilityRole="button"
              onPress={() => {
                setAdminTenantKeyDraft(tenant.tenantKey);
                setCreatedAdminSubjectKey(null);
                setCreateAdminError(null);
              }}
            >
              <Text
                style={{
                  color: theme.accent,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: tokens.typography.fontSize.xs,
                  fontWeight: "600",
                }}
              >
                Use for admin
              </Text>
            </Pressable>
          </View>
        </View>
        <View
          style={{
            flexDirection: "row",
            flexWrap: "wrap",
            gap: tokens.spacing.sm,
          }}
        >
          {renderCountTile("Patients", tenant.counts.patients)}
          {renderCountTile("Workforce", tenant.counts.workforceSubjects)}
          {renderCountTile("Conversations", tenant.counts.conversations)}
          {renderCountTile("Campaigns", tenant.counts.outboundCampaigns)}
        </View>
      </View>
    );
  };

  const sessionSummaryText = activeWorkforceSession
    ? `${activeWorkforceSession.displayName ?? "Platform operator"} · ${activeWorkforceSession.roleKeys.join(", ")}`
    : "Platform operator";

  return (
    <View
      style={[
        styles.root,
        createWebViewportMinHeightStyle(),
        {
          backgroundColor: theme.background,
          position: "relative",
        },
      ]}
    >
      <View
        pointerEvents="none"
        style={[styles.backgroundWash, { backgroundColor: theme.background }]}
      />
      <ScrollView
        contentContainerStyle={[
          styles.scrollContent,
          {
            gap: tokens.spacing.xl,
            padding: tokens.spacing.lg,
          },
        ]}
      >
        <View
          style={[
            styles.pageFrame,
            { gap: tokens.spacing.lg, maxWidth: 960 },
          ]}
        >
          <View
            style={[
              styles.headerCard,
              {
                backgroundColor: theme.surface,
                borderColor: theme.borderSubtle,
                borderRadius: layout.cardRadius,
                gap: tokens.spacing.sm,
                padding: tokens.spacing.lg,
              },
            ]}
          >
            <View
              style={{
                alignItems: isMediumLayout ? "center" : "flex-start",
                flexDirection: isMediumLayout ? "row" : "column",
                gap: tokens.spacing.md,
                justifyContent: "space-between",
              }}
            >
              <View style={[styles.headerContent, { gap: 4 }]}>
                <Text
                  style={{
                    color: theme.textPrimary,
                    fontFamily: layout.displayFontFamily,
                    fontSize: tokens.typography.fontSize.xl,
                    fontWeight: "700",
                  }}
                >
                  Platform Operator Console
                </Text>
                <Text
                  style={{
                    color: theme.textSecondary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: tokens.typography.fontSize.sm,
                  }}
                >
                  {sessionSummaryText}
                </Text>
              </View>
              <View
                style={[
                  styles.headerAside,
                  { flexDirection: "row", gap: tokens.spacing.sm },
                ]}
              >
                {canSwitchWorkspaces
                  ? renderWorkbenchActionButton({
                      disabled: false,
                      iconName: "chevron-right",
                      label: "Switch workspace",
                      onPress: beginWorkspaceSelection,
                      size: "compact",
                      theme,
                      tokens,
                      tone: "info",
                    })
                  : null}
                {renderWorkbenchActionButton({
                  disabled: false,
                  iconName: "stop",
                  label: "Sign out",
                  onPress: onSignOut,
                  size: "compact",
                  theme,
                  tokens,
                  tone: "warning",
                })}
              </View>
            </View>
          </View>

          <View style={[styles.listCard, cardBaseStyle, { alignSelf: "stretch" }]}>
            <View
              style={{
                alignItems: "center",
                flexDirection: "row",
                gap: tokens.spacing.sm,
                justifyContent: "space-between",
              }}
            >
              <Text
                style={[
                  styles.sectionTitle,
                  {
                    color: theme.textPrimary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: tokens.typography.fontSize.lg,
                  },
                ]}
              >
                Tenants
              </Text>
              {renderWorkbenchActionButton({
                disabled: isLoading,
                iconName: "chevron-right",
                label: isLoading ? "Refreshing…" : "Refresh",
                onPress: handleRefresh,
                size: "compact",
                theme,
                tokens,
                tone: "accent",
              })}
            </View>

            {generatedAt
              ? renderInlineMessage(
                  `Updated ${new Date(generatedAt).toLocaleString()}`,
                  theme.textTertiary,
                )
              : null}

            {isLoading
              ? renderInlineMessage("Loading tenants…", theme.textSecondary)
              : null}

            {!isLoading && loadErrorMessage
              ? renderInlineMessage(loadErrorMessage, theme.danger)
              : null}

            {!isLoading && !loadErrorMessage && tenants.length === 0
              ? renderInlineMessage(
                  "No tenants yet. Create the first one below.",
                  theme.textSecondary,
                )
              : null}

            {!isLoading && !loadErrorMessage && tenants.length > 0 ? (
              <View style={{ gap: tokens.spacing.sm }}>
                {tenants.map((tenant) => renderTenantRow(tenant))}
              </View>
            ) : null}
          </View>

          <View style={[styles.listCard, cardBaseStyle, { alignSelf: "stretch" }]}>
            <Text
              style={[
                styles.sectionTitle,
                {
                  color: theme.textPrimary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: tokens.typography.fontSize.lg,
                },
              ]}
            >
              Create tenant
            </Text>
            {renderField({
              labelText: "Tenant key",
              value: tenantKeyDraft,
              onChangeText: setTenantKeyDraft,
              placeholder: "acme-health",
              editable: !isCreatingTenant,
              autoCapitalizeNone: true,
            })}
            {renderField({
              labelText: "Tenant slug",
              value: tenantSlugDraft,
              onChangeText: setTenantSlugDraft,
              placeholder: "acme",
              editable: !isCreatingTenant,
              autoCapitalizeNone: true,
            })}
            {renderField({
              labelText: "Display name",
              value: tenantNameDraft,
              onChangeText: setTenantNameDraft,
              placeholder: "Acme Health",
              editable: !isCreatingTenant,
            })}
            {createTenantError
              ? renderInlineMessage(createTenantError, theme.danger)
              : null}
            {createTenantNotice
              ? renderInlineMessage(createTenantNotice, theme.success)
              : null}
            {renderWorkbenchActionButton({
              disabled: isCreatingTenant,
              iconName: "check",
              label: isCreatingTenant ? "Creating…" : "Create tenant",
              onPress: handleCreateTenant,
              theme,
              tokens,
              tone: "success",
            })}
          </View>

          <View style={[styles.listCard, cardBaseStyle, { alignSelf: "stretch" }]}>
            <Text
              style={[
                styles.sectionTitle,
                {
                  color: theme.textPrimary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: tokens.typography.fontSize.lg,
                },
              ]}
            >
              Create first admin
            </Text>
            {renderField({
              labelText: "Tenant key",
              value: adminTenantKeyDraft,
              onChangeText: setAdminTenantKeyDraft,
              placeholder: "acme-health",
              editable: !isCreatingAdmin,
              autoCapitalizeNone: true,
            })}
            {renderField({
              labelText: "Email",
              value: adminEmailDraft,
              onChangeText: setAdminEmailDraft,
              placeholder: "admin@acme.example",
              editable: !isCreatingAdmin,
              isEmail: true,
            })}
            {renderField({
              labelText: "Display name",
              value: adminNameDraft,
              onChangeText: setAdminNameDraft,
              placeholder: "Acme Administrator",
              editable: !isCreatingAdmin,
            })}
            {renderField({
              labelText: "Temporary password",
              value: adminPasswordDraft,
              onChangeText: setAdminPasswordDraft,
              placeholder: "Temporary password",
              editable: !isCreatingAdmin,
              secureTextEntry: true,
              autoCapitalizeNone: true,
            })}
            {createAdminError
              ? renderInlineMessage(createAdminError, theme.danger)
              : null}
            {createdAdminSubjectKey
              ? renderInlineMessage(
                  `Admin created. Subject key: ${createdAdminSubjectKey}`,
                  theme.success,
                )
              : null}
            {renderWorkbenchActionButton({
              disabled: isCreatingAdmin,
              iconName: "check",
              label: isCreatingAdmin ? "Creating…" : "Create first admin",
              onPress: handleCreateAdmin,
              theme,
              tokens,
              tone: "success",
            })}
          </View>
        </View>
      </ScrollView>
    </View>
  );
}
